/**
 * D-125 — "ממתין להחלטה" markers: the ONE registry.
 *
 * Source of truth for the texts and locations: OTE_ALIGNMENT/PENDING_MARKERS.md
 * (written by the decisions session). Each marker on the site is
 * `<PendingDecision id="PM-NN" />` and reads its entry here.
 *
 * When a topic closes (✅ in PENDING_MARKERS.md): set `active: false` on its
 * line, or delete the line. Either way the marker renders nothing, everywhere.
 * The bubble texts are i18n keys by convention:
 *   pendingDecision.items.<id>.decide   — what needs to be decided
 *   pendingDecision.items.<id>.current  — the behaviour today
 *
 * Pure module (no React, no i18n), so the unit tests can import it.
 */

export type PendingDecisionId = `PM-${string}`;

export interface PendingDecisionEntry {
  /** false: the topic closed, the marker is hidden (same as deleting the line). */
  active: boolean;
  /** The decision / question ids the marker belongs to (shown small in the bubble). */
  topic: string;
  /** D-301 (PM-27): only the OTE manager (general manager) sees this marker. */
  managerOnly?: boolean;
}

export const PENDING_DECISIONS: Readonly<Record<string, PendingDecisionEntry>> = {
  'PM-01': { active: false, topic: 'D-78, D-86' },
  'PM-02': { active: true, topic: 'D-80, D-81' },
  'PM-03': { active: true, topic: 'Q-49ב' },
  'PM-04': { active: false, topic: 'D-87' },
  'PM-05': { active: false, topic: 'D-101' },
  'PM-06': { active: false, topic: 'D-120' },
  'PM-07': { active: false, topic: 'D-122' },
  'PM-08': { active: true, topic: 'D-124 / QX-52' },
  'PM-09': { active: false, topic: 'QX-56' },
  'PM-10': { active: false, topic: 'QX-57' },
  'PM-11': { active: false, topic: 'D-129 / QX-61א' },
  'PM-12': { active: false, topic: 'D-129 / QX-61ב' },
  'PM-13': { active: false, topic: 'D-131 / QX-64' },
  'PM-14': { active: false, topic: 'D-132 / QX-67' },
  'PM-15': { active: false, topic: 'D-132 / QX-68' },
  'PM-16': { active: false, topic: 'D-132 / QX-70' },
  // P-30a (T-49 א): next to the "employee access" tab on the portal-access page.
  'PM-17': { active: false, topic: 'D-151' },
  // FIX-B10 (T-48): the business profitability report, next to its title.
  'PM-18': { active: true, topic: 'D-169 / Q-147' },
  // P-31a (D-182 QX-117): the WhatsApp / Google Calendar connection still has to be checked.
  'PM-19': { active: true, topic: 'D-182 / QX-117' },
  // P-32a (D-184): hour-bank revenue / hour value (profitability report + hour-bank card).
  'PM-20': { active: false, topic: 'D-184 / QX-129, QX-130 -> closed D-218' },
  // P-32a (D-184): the "hour bank exhausted" notification, once per bank (notification settings).
  'PM-21': { active: true, topic: 'D-184 / QX-131' },
  // P-38b (D-192 §1): next to "לצרף AI לפגישה" and in the meeting summary.
  'PM-22': { active: true, topic: 'D-192' },
  // P-38b (D-192 §10): next to the meeting link field.
  'PM-23': { active: true, topic: 'D-192 / Q-163' },
  // DQ-448 (D-193 / Q-171): in a discussion, next to the "טיימר מענה" box - a full work timer and hour logging on a discussion are still open.
  'PM-24': { active: true, topic: 'D-193 / Q-171' },
  // FIX-B44 (D-240 sec.5): next to the "install the extension" button.
  'PM-25': { active: true, topic: 'D-240' },
  // FIX-BQ071 (D-282, QX-1291): a priced extra-hours request partly used is billed pro rata - may change.
  'PM-26': { active: true, topic: 'D-282 / QX-1291' },
  // FIX-D299 ס1 (D-301, QX-1300): AI cost split by time incl. the new split of overlapping time (D-299) - next to the AI cost in profitability.
  'PM-27': { active: true, topic: 'D-301 / QX-1300', managerOnly: true },
};

/**
 * P-31a (D-178..D-183): PM-01, 04, 05, 06, 07, 09..17 are closed (`active: false`),
 * the live ones are PM-02, 03, 08, 18, 19. The placements stay in the code and
 * render nothing; the per-kind helpers below return only live ids, so a closed
 * topic never leaves an empty slot behind.
 */
const live = (ids: PendingDecisionId[]): PendingDecisionId[] => ids.filter((id) => PENDING_DECISIONS[id]?.active);

/**
 * Which markers sit on ONE line of a charge (the edit form and the view):
 *   - the server line ('server'): PM-11 (partial month) and PM-12 (in arrears
 *     or in advance); on a project / development charge also PM-15 (is the
 *     server revenue, or a pass-through);
 *   - a לל"ח "יחידות AI מחודש MM/YYYY" line (QX-64, the AI cost entered after
 *     the month was approved): PM-13.
 */
export function chargeLineMarkerIds(
  line: { line_kind?: string | null; description?: string | null },
  obligationType?: string | null,
): PendingDecisionId[] {
  if (line.line_kind === 'server') {
    return obligationType === 'project' || obligationType === 'development'
      ? live(['PM-11', 'PM-12', 'PM-15'])
      : live(['PM-11', 'PM-12']);
  }
  if (line.line_kind === 'ai_units' && /^יחידות AI מחודש \d{2}\/\d{4}/.test((line.description ?? '').trim())) {
    return live(['PM-13']);
  }
  // D-282 (QX-1291): an "שעות נוספות" line of a service-contract monthly charge.
  if (line.line_kind === 'overage' && obligationType === 'service_contract') return live(['PM-26']);
  return [];
}

/** D-282 (QX-1291): the marker next to an APPROVED extra-hours request of a service contract. */
export function extraHoursRequestMarkerIds(status?: string | null): PendingDecisionId[] {
  return status === 'approved' || status === 'overridden' ? live(['PM-26']) : [];
}

/**
 * Which markers sit next to a profitability row's revenue, by the revenue
 * kinds behind it (revenue.service, P-16): a project / development price
 * (installments) PM-14, its server PM-15, hour-bank usage PM-16.
 */
export function profitRowMarkerIds(revenueKinds?: readonly string[] | null): PendingDecisionId[] {
  const kinds = new Set(revenueKinds ?? []);
  const out: PendingDecisionId[] = [];
  if (kinds.has('installment')) out.push('PM-14');
  if (kinds.has('server')) out.push('PM-15');
  if (kinds.has('hour_bank_usage')) out.push('PM-16');
  return live(out);
}

export const pendingDecisionKeys = (id: PendingDecisionId) => ({
  decide: `pendingDecision.items.${id}.decide`,
  current: `pendingDecision.items.${id}.current`,
});

/**
 * Who sees the markers: OTE staff only (D-125), never a customer and never in
 * the portal. Staff is decided by the DB identity (employee profile), as the
 * server reports it on `GET /api/permissions/me` (`isOteEmployee`); an older
 * server without the flag falls back to the login's `is_ote_employee`. Unknown
 * means hidden: a marker is never shown on a guess.
 */
export interface PendingViewer {
  /** `/permissions/me` answered (or fell back); before that nothing shows. */
  ready: boolean;
  /** A customer identity (server `portalOnly`, or the login hint). */
  portalOnly: boolean;
  /** `isOteEmployee` from `/permissions/me`; undefined on an older server. */
  serverIsOteEmployee?: boolean;
  /** `is_ote_employee` from the login. */
  loginIsOteEmployee?: boolean;
  /** The current route is the customer portal. */
  inPortal?: boolean;
  /** D-301: the viewer is the OTE manager (for `managerOnly` markers; unknown = not). */
  isGeneralManager?: boolean;
}

export function canSeePendingMarkers(v: PendingViewer): boolean {
  if (!v.ready || v.portalOnly || v.inPortal) return false;
  if (v.serverIsOteEmployee !== undefined) return v.serverIsOteEmployee === true;
  return v.loginIsOteEmployee === true;
}

/** The entry to render, or null (unknown id, closed topic, or a viewer who may not see it). */
export function resolvePendingDecision(
  id: string,
  viewer: PendingViewer,
  registry: Readonly<Record<string, PendingDecisionEntry>> = PENDING_DECISIONS,
): (PendingDecisionEntry & { id: PendingDecisionId }) | null {
  if (!canSeePendingMarkers(viewer)) return null;
  const entry = Object.prototype.hasOwnProperty.call(registry, id) ? registry[id] : undefined;
  if (!entry || !entry.active) return null;
  if (entry.managerOnly && viewer.isGeneralManager !== true) return null;
  return { ...entry, id: id as PendingDecisionId };
}
